/**
 * What a single dial of the Hubject bridge says, in words.
 *
 * The outcome a person needs is not the status word: it is whether a session
 * was opened, whether it was closed again, and if it was not, which id is
 * still open at Hubject. Their four statuses collapse into three situations,
 * and the third is the only one that needs somebody to act.
 */
export type DialResult = {
  error?: string
  correlation_id?: string
  budget_ms?: number
  authorize?: {
    ok: boolean
    status: string | null
    accepted: boolean
    timed_out: boolean
    code: string | null
    detail: string | null
    retryable: boolean
    hubject_session_id: string | null
    elapsed_ms: number
  }
  released?: {
    needed: boolean
    ok?: boolean
    status?: string | null
    detail?: string | null
    elapsed_ms?: number
    open_session_id?: string | null
    why_not?: string
  }
}

export function dialInWords(result: DialResult): string {
  if (result.error) return `The console could not ask: ${result.error}`
  const authorize = result.authorize
  if (!authorize) return 'No answer was returned.'

  if (!authorize.ok) {
    return `Not sent, or no answer: ${authorize.detail ?? 'no detail'}`
  }
  if (authorize.timed_out) {
    // Their Timeout keeps no session, so there is nothing for anybody to do.
    // Said plainly, because a reader who does not know that goes looking for
    // a session to close.
    return 'Hubject did not answer in time. No session is open.'
  }
  if (!authorize.accepted) {
    return `Hubject answered ${authorize.status ?? 'nothing usable'}. No session was opened.`
  }

  const released = result.released
  if (released?.ok) {
    return 'Accepted, then released. No session is open.'
  }
  // THE ONE CASE THAT NEEDS A PERSON, and the id is the only way to close it.
  return (
    `Accepted, but the release failed: ${released?.detail ?? 'no detail'}. ` +
    `Session ${released?.open_session_id ?? 'unknown'} is still open at Hubject.`
  )
}

/** Whether this result needs somebody to do something now. */
export function needsAttention(result: DialResult): boolean {
  if (result.error) return false
  return Boolean(result.authorize?.accepted && result.released?.ok === false)
}
